import {
  alpha,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  useTheme,
} from "@mui/material";
import {
  Close,
  Edit as EditIcon,
  EventBusy,
  EventAvailable,
} from "@mui/icons-material";
import * as React from "react";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { updateAttendance as updateAttendanceApi } from "apis";
import { useSnackbar } from "notistack";
import { useSelector } from "react-redux";
import { RootState } from "store";
import { AuthState } from "store/features/auth";
import { useTranslation } from "react-i18next";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import "dayjs/locale/ar";
import "dayjs/locale/en";
import "dayjs/locale/fr";
import { IChild } from "types";
import { stringAvatar } from "utils";
import { isRoomLeaderOrAdmin } from "utils/roles";

type Attendance = {
  id: number;
  child: IChild;
  type: number;
  reason: string;
  comment: string;
  startDate: string;
  endDate: string;
  time: string;
  status: number;
};

interface Props {
  open: boolean;
  onClose: () => void;
  child: IChild[];
  item: Attendance;
}

interface FormFields {
  id: number;
  child_id: number;
  attendance: number;
  time: "morning" | "afternoon" | "whole_day";
  reason: string;
  comment: string;
  status: number;
}

const EditAttendanceDialog: React.FC<Props> = ({
  open,
  onClose,
  item,
  child,
}) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const authInfo = useSelector<RootState, AuthState>((store) => store.auth);
  const { enqueueSnackbar } = useSnackbar();

  const [locale, setLocale] = useState(i18n.language);
  const [selectedChildId, setSelectedChildId] = useState<string>(
    String(item.child.id),
  );
  const [attendanceMode, setAttendanceMode] = useState(
    item.child.attendanceMode ? "1" : "0",
  );
  const [submitting, setSubmitting] = useState(false);

  const { register, control, handleSubmit, watch } = useForm<FormFields>({
    defaultValues: {
      ...(item as unknown as FormFields),
      attendance: item.type,
      time:
        item.time === "morning" || item.time === "afternoon"
          ? item.time
          : "whole_day",
      reason: item.reason || "sickness",
      status: item.status,
    },
  });

  const attendance = String(watch("attendance"));

  const tomorrow = dayjs().add(1, "day");
  const [dateFrom, setDateFrom] = useState<Dayjs | null>(dayjs(item.startDate));
  const [dateTo, setDateTo] = useState<Dayjs | null>(dayjs(item.endDate));

  const isAbsence = attendance === "0";

  useEffect(() => {
    setLocale(i18n.language);
  }, [i18n.language]);

  useEffect(() => {
    if (selectedChildId) {
      const c = child.find((el) => String(el.id) === String(selectedChildId));
      setAttendanceMode(c?.attendanceMode ? "1" : "0");
    }
  }, [selectedChildId, child]);

  const onSubmit: SubmitHandler<FormFields> = (data) => {
    if (dateFrom && dateTo && dateFrom > dateTo) {
      enqueueSnackbar(t("attendance.date_error"), { variant: "error" });
      return;
    }

    setSubmitting(true);
    updateAttendanceApi({
      infos: data,
      startDate: dateFrom as Dayjs,
      endDate: dateTo as Dayjs,
    })
      .then(() => {
        enqueueSnackbar(
          t("attendance.updated") || "Record updated successfully",
          { variant: "success" },
        );
        onClose();
      })
      .catch((err) => {
        enqueueSnackbar(err?.response?.data?.message || "Failed to update", {
          variant: "error",
        });
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      {/* Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
          color: "common.white",
          px: 3,
          py: 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <EditIcon />
          <Typography variant="h6" fontWeight={700}>
            {t("attendance.edit") || "Edit Request"}
          </Typography>
          <Chip
            label={isAbsence ? t("labels.absence") : t("labels.extra_session")}
            icon={
              isAbsence ? (
                <EventBusy sx={{ fontSize: 14 }} />
              ) : (
                <EventAvailable sx={{ fontSize: 14 }} />
              )
            }
            size="small"
            sx={{
              bgcolor: alpha(theme.palette.common.white, 0.2),
              color: "common.white",
              fontWeight: 600,
              fontSize: 11,
              "& .MuiChip-icon": { color: "common.white" },
            }}
          />
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: "common.white" }}
        >
          <Close />
        </IconButton>
      </Box>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent sx={{ px: 3, py: 3 }}>
          {/* Child selector as chips */}
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={600}
            sx={{
              mb: 1,
              display: "block",
              textTransform: "uppercase",
              letterSpacing: 0.5,
            }}
          >
            {t("timeline.child")}
          </Typography>
          <Box display="flex" gap={1} flexWrap="wrap" mb={3}>
            {child.map((ch) => (
              <Chip
                key={ch.id}
                avatar={
                  ch.photo ? (
                    <Avatar src={ch.photo as string} />
                  ) : (
                    <Avatar
                      {...stringAvatar(`${ch.firstName} ${ch.lastName}`, {
                        width: 24,
                        height: 24,
                        fontSize: 11,
                      })}
                    />
                  )
                }
                label={`${ch.firstName} ${ch.lastName}`}
                onClick={() => {
                  setSelectedChildId(String(ch.id));
                  register("child_id").onChange({
                    target: { value: ch.id, name: "child_id" },
                  });
                }}
                variant={
                  String(selectedChildId) === String(ch.id)
                    ? "filled"
                    : "outlined"
                }
                color={
                  String(selectedChildId) === String(ch.id)
                    ? "primary"
                    : "default"
                }
                sx={{
                  fontWeight:
                    String(selectedChildId) === String(ch.id) ? 600 : 400,
                  transition: "all 0.2s",
                  cursor: "pointer",
                }}
              />
            ))}
          </Box>

          {/* Date range */}
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={600}
            sx={{
              mb: 1,
              display: "block",
              textTransform: "uppercase",
              letterSpacing: 0.5,
            }}
          >
            {t("labels.date")}
          </Typography>
          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            adapterLocale={locale}
          >
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6}>
                <DatePicker
                  label={t("labels.from")}
                  value={dateFrom}
                  minDate={tomorrow}
                  inputFormat="DD MMM YYYY"
                  onChange={(v) => setDateFrom(v)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      fullWidth
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <DatePicker
                  label={t("labels.to")}
                  value={dateTo}
                  minDate={dateFrom ?? tomorrow}
                  inputFormat="DD MMM YYYY"
                  onChange={(v) => setDateTo(v)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      fullWidth
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </LocalizationProvider>

          {/* Type & Time */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>{t("inputs.type")}</InputLabel>
                <Controller
                  name="attendance"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      label={t("inputs.type")}
                      sx={{ borderRadius: 2 }}
                    >
                      <MenuItem value={0}>{t("labels.absence")}</MenuItem>
                      {attendanceMode === "1" && (
                        <MenuItem value={1}>
                          {t("labels.extra_session")}
                        </MenuItem>
                      )}
                    </Select>
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>{t("labels.time")}</InputLabel>
                <Controller
                  name="time"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      label={t("labels.time")}
                      sx={{ borderRadius: 2 }}
                    >
                      <MenuItem value="whole_day">
                        {t("labels.whole_day")}
                      </MenuItem>
                      <MenuItem value="morning">{t("labels.morning")}</MenuItem>
                      <MenuItem value="afternoon">
                        {t("labels.afternoon")}
                      </MenuItem>
                    </Select>
                  )}
                />
              </FormControl>
            </Grid>
          </Grid>

          {/* Reason (absences only) */}
          {isAbsence && (
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>{t("labels.reason")}</InputLabel>
              <Controller
                name="reason"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    label={t("labels.reason")}
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="sickness">{t("labels.sickness")}</MenuItem>
                    <MenuItem value="on_holiday">
                      {t("labels.on_holiday")}
                    </MenuItem>
                    <MenuItem value="other">{t("labels.other")}</MenuItem>
                  </Select>
                )}
              />
            </FormControl>
          )}

          {/* Comment */}
          <TextField
            label={t("inputs.comment")}
            {...register("comment")}
            fullWidth
            multiline
            rows={3}
            size="small"
            sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          />

          {/* Status */}
          <FormControl fullWidth size="small">
            <InputLabel>{t("labels.status")}</InputLabel>
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  label={t("labels.status")}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value={0}>{t("labels.pending")}</MenuItem>
                  {isRoomLeaderOrAdmin(authInfo.role) && (
                    <MenuItem value={1}>{t("labels.approved")}</MenuItem>
                  )}
                  {isRoomLeaderOrAdmin(authInfo.role) && (
                    <MenuItem value={2}>{t("labels.declined")}</MenuItem>
                  )}
                </Select>
              )}
            />
          </FormControl>
        </DialogContent>

        {/* Actions */}
        <Box sx={{ px: 3, pb: 3, display: "flex", gap: 1.5 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            fullWidth
            sx={{
              borderRadius: 2,
              py: 1.2,
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            {t("buttons.cancel")}
          </Button>
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={submitting}
            sx={{
              borderRadius: 2,
              py: 1.2,
              textTransform: "none",
              fontWeight: 600,
              boxShadow: "none",
              "&:hover": { boxShadow: 2 },
            }}
          >
            {submitting ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              t("buttons.update")
            )}
          </Button>
        </Box>
      </form>
    </Dialog>
  );
};

export default EditAttendanceDialog;
